// Top key visual slider.
// Handles centered slide positioning, autoplay, arrow/dot controls, swipe, and infinite-loop clones.
class OishiiSlider {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-oishii-slider-viewport]');
    this.track = root.querySelector('[data-oishii-slider-track]');
    this.slides = [...root.querySelectorAll('[data-oishii-slide]')];
    this.controls = [...root.querySelectorAll('[data-oishii-slide-to]')];
    this.index = Math.max(0, Math.min(this.slides.length - 1, Number(root.dataset.initialIndex || 0)));
    this.visualIndex = this.slides.length > 1 ? this.index + 1 : this.index;
    this.trackSlides = [];
    this.pendingSnapIndex = null;
    this.isAnimating = false;
    this.transitionFallback = null;
    this.timer = null;
    this.pointerStartX = null;

    // Wire up controls and viewport events once per slider instance.
    root.querySelector('[data-oishii-prev]')?.addEventListener('click', () => this.step(-1, true));
    root.querySelector('[data-oishii-next]')?.addEventListener('click', () => this.step(1, true));
    this.controls.forEach((control) => control.addEventListener('click', () => this.go(Number(control.dataset.oishiiSlideTo), true)));
    this.track?.addEventListener('transitionend', (event) => this.handleTransitionEnd(event));
    this.viewport?.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
    window.addEventListener('pointerup', (event) => this.handlePointerUp(event));
    window.addEventListener('resize', () => this.render({ animate: false }));
    root.addEventListener('mouseenter', () => window.clearInterval(this.timer));
    root.addEventListener('mouseleave', () => this.startAutoplay());
    root.addEventListener('focusin', () => window.clearInterval(this.timer));
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) this.startAutoplay();
    });

    this.setupInfiniteTrack();
    this.render({ animate: false });
    this.startAutoplay();
  }

  step(direction, restart = false) {
    if (this.isAnimating) {
      if (restart) this.startAutoplay();
      return;
    }

    this.go(this.index + direction, restart);
  }

  go(index, restart = false) {
    if (!this.slides.length) return;
    if (this.slides.length === 1) {
      this.index = 0;
      this.visualIndex = 0;
      this.render();
      return;
    }

    const lastIndex = this.slides.length - 1;
    const targetIndex = (index + this.slides.length) % this.slides.length;

    if (this.isAnimating) {
      if (restart) this.startAutoplay();
      return;
    }

    if (targetIndex === this.index && index >= 0 && index <= lastIndex) {
      if (restart) this.startAutoplay();
      return;
    }

    this.index = targetIndex;
    this.pendingSnapIndex = null;

    // Move to the clone first when crossing either edge, then snap back to the real slide after the animation.
    if (index < 0) {
      this.visualIndex = 0;
      this.pendingSnapIndex = lastIndex + 1;
    } else if (index > lastIndex) {
      this.visualIndex = this.slides.length + 1;
      this.pendingSnapIndex = 1;
    } else {
      this.visualIndex = index + 1;
    }

    this.render();
    this.isAnimating = true;
    window.clearTimeout(this.transitionFallback);
    this.transitionFallback = window.setTimeout(() => this.finishTransition(), 700);
    if (restart) this.startAutoplay();
  }

  render({ animate = true } = {}) {
    const slide = this.trackSlides[this.visualIndex] || this.slides[this.index];
    if (!slide || !this.viewport || !this.track) return;

    // Keep the active slide centered inside the viewport regardless of screen width.
    this.track.style.transition = animate ? '' : 'none';
    const offset = this.viewport.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    this.slides.forEach((item, index) => {
      item.toggleAttribute('data-active', index === this.index);
      item.inert = index !== this.index;
    });
    this.controls.forEach((control) => control.setAttribute('aria-current', String(Number(control.dataset.oishiiSlideTo) === this.index)));
  }

  startAutoplay() {
    window.clearInterval(this.timer);
    if (this.root.matches(':hover') || this.root.contains(document.activeElement)) return;
    const seconds = Number(this.root.dataset.autoplay || 0);
    const shouldReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Do not autoplay when the user has requested reduced motion.
    if (seconds > 0 && this.slides.length > 1 && !shouldReduceMotion) {
      this.timer = window.setInterval(() => this.step(1), seconds * 1000);
    }
  }

  setupInfiniteTrack() {
    if (!this.track || this.slides.length <= 1) {
      this.trackSlides = this.slides;
      return;
    }

    const firstClone = this.createClone(this.slides[0]);
    const lastClone = this.createClone(this.slides[this.slides.length - 1]);

    // One clone on each side is enough for a seamless infinite loop.
    this.track.prepend(lastClone);
    this.track.append(firstClone);
    this.trackSlides = [...this.track.children];
  }

  createClone(slide) {
    const clone = slide.cloneNode(true);
    clone.removeAttribute('data-oishii-slide');
    clone.setAttribute('data-oishii-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;

    // Clones are visual only. Remove Shopify editor attributes and focusable elements from tab order.
    [...clone.attributes].forEach((attribute) => {
      if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
    });

    clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    return clone;
  }

  handleTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.finishTransition();
  }

  finishTransition() {
    if (!this.isAnimating && this.pendingSnapIndex === null) return;

    window.clearTimeout(this.transitionFallback);

    if (this.pendingSnapIndex !== null) {
      this.visualIndex = this.pendingSnapIndex;
      this.pendingSnapIndex = null;
      this.render({ animate: false });
      this.track.offsetHeight;
      this.track.style.transition = '';
    }

    this.isAnimating = false;
  }

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointerStartX = event.clientX;
  }

  handlePointerUp(event) {
    if (this.pointerStartX === null) return;
    const delta = event.clientX - this.pointerStartX;
    this.pointerStartX = null;
    if (Math.abs(delta) < 40) return;
    this.step(delta < 0 ? 1 : -1, true);
  }
}

function initOishiiHome(scope = document) {
  // Initialize key visual sliders. The data flag prevents duplicate binding in the theme editor.
  scope.querySelectorAll('[data-oishii-slider]:not([data-initialized])').forEach((root) => {
    root.dataset.initialized = 'true';
    new OishiiSlider(root);
  });

  scope.querySelectorAll('[data-oishii-rail]:not([data-initialized])').forEach((root) => {
    root.dataset.initialized = 'true';
    const rail = root.querySelector('[data-oishii-rail-track]');
    if (!rail) return;

    // Product rails use duplicated card sets so the arrows can loop endlessly.
    const originals = [...rail.children];
    if (originals.length > 1) {
      const createClone = (card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-oishii-rail-clone', 'true');

        // Cloned product cards should not be editable/focusable duplicates.
        [...clone.attributes].forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
        });

        clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });

        return clone;
      };

      rail.prepend(...originals.map(createClone));
      rail.append(...originals.map(createClone));
    }

    const getRailStep = () => {
      const cards = [...rail.children];
      if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;

      // Fallback for a single card: card width plus CSS gap.
      const card = cards[0];
      const styles = window.getComputedStyle(rail);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card ? card.getBoundingClientRect().width + gap : 0;
    };
    const getRailMetrics = () => {
      const cards = [...rail.children];
      const firstOriginal = cards[originals.length];
      const firstAfterClone = cards[originals.length * 2];
      const start = firstOriginal ? firstOriginal.offsetLeft : 0;
      const end = firstAfterClone ? firstAfterClone.offsetLeft : rail.scrollWidth;
      const setWidth = Math.max(0, end - start);

      // start/end mark the real product set between the prepended and appended clones.
      return { start, end, setWidth };
    };
    const jumpTo = (left) => {
      // Jumping without smooth behavior is used for invisible loop resets.
      rail.style.scrollBehavior = 'auto';
      rail.scrollLeft = left;
      rail.offsetHeight;
      rail.style.scrollBehavior = '';
    };
    const normalizeRail = () => {
      if (originals.length <= 1) return;
      const { start, end, setWidth } = getRailMetrics();
      if (!setWidth) return;

      // If scrolling reaches a clone set, jump back into the matching real set.
      if (rail.scrollLeft < start - 1) {
        jumpTo(rail.scrollLeft + setWidth);
      } else if (rail.scrollLeft >= end - 1) {
        jumpTo(rail.scrollLeft - setWidth);
      }
    };

    let lockedScrollLeft = 0;

    if (originals.length > 1) {
      window.requestAnimationFrame(() => {
        // Start on the real set, not on the prepended clone set.
        lockedScrollLeft = getRailMetrics().start;
        jumpTo(lockedScrollLeft);
      });
    }

    let normalizeTimer = null;
    let isProgrammaticScroll = false;
    const move = (direction) => {
      // Arrow buttons move exactly one card at a time.
      normalizeRail();
      const step = getRailStep();
      if (!step) return;
      isProgrammaticScroll = true;
      rail.scrollTo({ left: rail.scrollLeft + direction * step, behavior: 'smooth' });
      window.clearTimeout(normalizeTimer);
      normalizeTimer = window.setTimeout(() => {
        normalizeRail();
        lockedScrollLeft = rail.scrollLeft;
        isProgrammaticScroll = false;
      }, 450);
    };
    rail.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (event.buttons) event.preventDefault();
    });
    rail.addEventListener('scroll', () => {
      // Product rails are button-operated only. User-driven scroll/flick is reset.
      if (!isProgrammaticScroll) {
        jumpTo(lockedScrollLeft);
        return;
      }
      window.clearTimeout(normalizeTimer);
      normalizeTimer = window.setTimeout(() => {
        normalizeRail();
        lockedScrollLeft = rail.scrollLeft;
        isProgrammaticScroll = false;
      }, 120);
    }, { passive: true });
    root.querySelector('[data-oishii-rail-prev]')?.addEventListener('click', () => move(-1));
    root.querySelector('[data-oishii-rail-next]')?.addEventListener('click', () => move(1));
  });
}

document.addEventListener('DOMContentLoaded', () => initOishiiHome());
// Re-initialize when Shopify theme editor reloads a section.
document.addEventListener('shopify:section:load', (event) => initOishiiHome(event.target));
